import React from 'react'
import {
  axisClasses,
  BarChart,
  LegendRendererProps,
  LineChart,
  pieArcLabelClasses,
  PieChart,
} from '@mui/x-charts'
import { DefaultizedPieValueType } from '@mui/x-charts/models/seriesType/pie'
import { formatCurrency } from 'Components/Charts/CampaignMultiLineChart/utils'
import {
  ChartSeriesData,
  Goal,
  GoalType,
  MetricType,
} from 'Generated/graphql-types'
import { resiliaPalette } from 'Components/Pages/FunderDashboard/FunderGoals/utils'
import { ChartAxisOptions } from './ChartAxisOptions'
import {
  Chart,
  ChartTypeOptionsEnum,
  DimensionsData,
  SeriesData,
} from '../types'
import { calculateAdditionalChartSeries, formatNumberCompact } from '../utils'

type GoalChartProps = {
  chart?: Chart
  chartType: ChartTypeOptionsEnum
  goal: Goal
  pieChartSeriesData: ChartSeriesData[]
  series: SeriesData[] | DimensionsData[]
  campaignTarget?: number
  height?: number
}

// Helper function to format dates for x-axis
const formatDateForAxis = (date: string | Date): string => {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return new Date(date).toLocaleDateString(undefined, options)
}

// Helper function to determine appropriate tick count based on data points
const getAppropriateTickCount = (dates: Date[]): number => {
  const count = dates.length
  if (count <= 6) return count
  if (count <= 12) return Math.ceil(count / 2)
  if (count <= 24) return Math.ceil(count / 4)
  return Math.ceil(count / 6)
}

export const GoalChart = ({
  chart,
  chartType,
  goal,
  pieChartSeriesData,
  series,
  campaignTarget,
  height,
}: GoalChartProps) => {
  const chartHeight = height || 500
  const dateStrings = series[0].data.map(({ x }) => x)

  // Parse the date strings to Date objects for proper time scale
  const dateObjects = dateStrings.map((dateStr) => new Date(dateStr))

  // Calculate appropriate number of ticks based on data length
  const tickCount = getAppropriateTickCount(dateObjects)

  const shouldAverage = goal.metric === MetricType.Average
  const calculatedSeries = calculateAdditionalChartSeries(series, shouldAverage)

  // Create series data in the format expected by MUI X-Charts
  const seriesData = (series as SeriesData[]).map((category) => ({
    data: category.data.map(({ y }) => y),
    label: category.title,
  }))

  const allSeries =
    seriesData.length === 1 ? seriesData : [calculatedSeries, ...seriesData]

  // AML: Seems like this should look for the target in the chart first, seemingly goal.target has been deprecated
  const target = campaignTarget || chart?.target || goal.target
  const isCampaignGoal = goal.type === GoalType.Campaign
  const yAxisLabel = isCampaignGoal
    ? 'Net Raised ($)'
    : chart?.unitOfMeasurement

  const legendSlotProps: Partial<LegendRendererProps> = {
    direction: chartHeight < 500 ? 'row' : ('column' as const),
    itemMarkWidth: 15,
    itemMarkHeight: 5,
    position: { vertical: 'top', horizontal: 'right' },
    padding: -5,
  }

  switch (chartType) {
    case ChartTypeOptionsEnum.pie: {
      const data = pieChartSeriesData.map((serie, index) => {
        const values = serie.data.map((dataPoint) => dataPoint?.y ?? 0)
        const value = values.reduce((acc, v) => acc + v, 0) || 0
        return {
          id: index,
          value,
          label: serie.title,
        }
      })

      const totalValue = data.reduce((acc, { value }) => acc + value, 0)

      const pieChartData = [
        {
          data,
          arcLabel: (item: DefaultizedPieValueType) =>
            `${((item.value / totalValue) * 100).toFixed(1)}%`,
        },
      ]

      return (
        <PieChart
          colors={resiliaPalette}
          height={chartHeight}
          series={pieChartData}
          slotProps={{ legend: legendSlotProps }}
          sx={{
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontWeight: 'bold',
            },
          }}
        />
      )
    }
    case ChartTypeOptionsEnum.bar:
      return (
        <BarChart
          colors={resiliaPalette}
          series={allSeries.map((s) => ({
            ...s,
            valueFormatter: (value: number | null) =>
              // eslint-disable-next-line no-nested-ternary
              value === null
                ? 'No data'
                : isCampaignGoal
                ? formatCurrency(value)
                : formatNumberCompact(value),
          }))}
          height={chartHeight}
          slotProps={{ legend: legendSlotProps }}
          xAxis={[
            {
              id: 'Dates',
              data: dateStrings.map(formatDateForAxis),
              scaleType: 'band',
              tickInterval: (_, index) =>
                index % Math.ceil(dateStrings.length / tickCount) === 0,
            },
          ]}
          sx={{
            [`& .${axisClasses.left} .${axisClasses.label}`]: {
              transform: 'translateX(-10px)',
            },
          }}
          yAxis={[
            {
              valueFormatter: formatNumberCompact,
              min: 0,
              max:
                Math.max(target ?? 0, ...allSeries.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
        >
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </BarChart>
      )
    default:
      return (
        <LineChart
          colors={resiliaPalette}
          height={chartHeight}
          xAxis={[
            {
              data: dateObjects,
              scaleType: 'time',
              tickLabelStyle: {
                angle: 45,
                textAnchor: 'start' as const,
                fontSize: 12,
              },
              // Limit the number of ticks displayed
              tickNumber: tickCount,
              // Format the date labels to be more concise
              valueFormatter: (date) => formatDateForAxis(date),
            },
          ]}
          series={allSeries.map((s) => ({
            ...s,
            valueFormatter: (value: number | null) =>
              // eslint-disable-next-line no-nested-ternary
              value === null
                ? 'No data'
                : isCampaignGoal
                ? formatCurrency(value)
                : formatNumberCompact(value),
            connectNulls: true, // Connect across null values for smoother lines
          }))}
          slotProps={{ legend: legendSlotProps }}
          sx={{
            [`& .${axisClasses.left} .${axisClasses.label}`]: {
              transform: 'translateX(-10px)',
            },
          }}
          yAxis={[
            {
              valueFormatter: formatNumberCompact,
              min: 0,
              max:
                Math.max(target ?? 0, ...allSeries.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
        >
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </LineChart>
      )
  }
}
